'use client';

import { Controller, FormProvider } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form';
import { Button, SelectDropdown, Input, TextArea, CheckBox, Typography } from '@/app/common';
import { Spinner } from '@workspace/ui/common';
import { Label } from '@/app/common/Label';
import type { ContactFormData } from '@workspace/interfaces/contact-form.types';
import type { ContactFormFieldsProps } from '../interfaces';
import {
  FORM_FIELD_KINDS,
  INPUT_TYPES,
  CONTACT_FORM_MAX_LENGTH_DEFAULT,
  INPUT_MAX_LENGTH,
  contactFormFieldsConfig,
} from '@workspace/constants/contact-form.constant';
import { formatPhoneAU, isValidPhoneAU, trimToMaxLengthAU } from '@/app/lib/helper';
import { getDropdownOptions } from '../lib/contact-form.utils';

export function ContactFormFields({
  form,
  loading,
  submitting = false,
  onMessageChange,
  onSubmit,
  onInvalidSubmit,
  children,
  fields,
  showForm = true,
}: ContactFormFieldsProps) {
  const maxLengthDefault = CONTACT_FORM_MAX_LENGTH_DEFAULT;
  const placeholderFallbacks = contactFormFieldsConfig.reduce(
    (acc, field) => {
      if (field.placeholder) {
        acc[field.name as string] = field.placeholder;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  if (loading) {
    return (
      <div className="font-openSans animate-pulse space-y-xl w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-lg gap-x-md">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="space-y-xs">
              <div className="h-4 w-24 rounded bg-secondary-100" />
              <div className="h-11 w-full rounded bg-secondary-100" />
            </div>
          ))}
        </div>
        <div className="h-11 w-full md:w-60 rounded bg-secondary-100" />
      </div>
    );
  }
  if (!showForm) {
    return <div className="flex-1 font-openSans max-w-none">{children}</div>;
  }

  return (
    <div className="flex-1 font-openSans max-w-none">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-xl">
          <Controller
            control={form.control}
            name="honeypot"
            render={({ field }) => (
              <input type="text" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} {...field} />
            )}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-lg gap-x-md">
            {fields?.map((f) => {
              const isEnquiryType = f.name === 'enquiry_type___dropdown';
              const col = f.fullWidth || isEnquiryType ? 'lg:col-span-2' : '';

              switch (f.kind) {
                case FORM_FIELD_KINDS.INPUT:
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      rules={
                        String(f.name) === 'phone' || String(f.name) === '0-2/phone'
                          ? {
                              validate: (v) => isValidPhoneAU(String(v || '')) || 'Invalid phone number.',
                            }
                          : undefined
                      }
                      render={({ field, fieldState }) => {
                        const handleBlur = () => {
                          field.onBlur();
                          form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                        };

                        const isPhoneField = String(f.name) === 'phone' || String(f.name) === '0-2/phone';

                        return (
                          <div className={`w-full flex-1 ${col} ${f.className ?? ''}`}>
                            <Label
                              text={f.label}
                              isRequired={!!f.required}
                              className="text-base text-secondary-400 font-semibold leading-6 mb-xs block"
                            />
                            {isPhoneField ? (
                              <Input
                                type="tel"
                                placeholder={
                                  (f.label ? `Enter your ${f.label.toLowerCase()}` : '') ||
                                  f.placeholder ||
                                  placeholderFallbacks[f.name as string] ||
                                  'Enter your phone number'
                                }
                                value={formatPhoneAU((field.value as string) ?? '')}
                                onChange={(val: string) => {
                                  field.onChange(trimToMaxLengthAU(val));
                                }}
                                onBlur={handleBlur}
                                status={fieldState.error ? 'error' : 'default'}
                                className="w-full max-w-none h-11 py-xs px-button border rounded-sm bg-white text-gray-900 focus-within:border-secondary-200 focus-within:shadow-focus"
                                inputClassName="placeholder:text-secondary-200!"
                                variant="field"
                              />
                            ) : (
                              <Input
                                type={f.type ?? INPUT_TYPES.TEXT}
                                placeholder={
                                  (f.label ? `Enter your ${f.label.toLowerCase()}` : '') ||
                                  f.placeholder ||
                                  placeholderFallbacks[f.name as string] ||
                                  ''
                                }
                                value={(field.value as string) ?? ''}
                                onChange={field.onChange}
                                onBlur={handleBlur}
                                maxLength={
                                  (INPUT_MAX_LENGTH as Partial<Record<keyof ContactFormData, number>>)[
                                    f.name as keyof ContactFormData
                                  ]
                                }
                                status={fieldState.error ? 'error' : 'default'}
                                className="w-full max-w-none h-11 py-xs px-button border rounded-sm bg-white text-gray-900 focus-within:border-secondary-200 focus-within:shadow-focus"
                                inputClassName="placeholder:text-secondary-200!"
                                variant="field"
                              />
                            )}
                            {fieldState.error && (
                              <Typography variant="bodySm" className="mt-xs !text-xs !text-error">
                                {fieldState.error.message}
                              </Typography>
                            )}
                          </div>
                        );
                      }}
                    />
                  );
                case FORM_FIELD_KINDS.DROPDOWN: {
                  const opts = f.options ?? getDropdownOptions(f.name);
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      render={({ field, fieldState }) => (
                        <div
                          className={`${isEnquiryType ? 'w-full space-y-xs' : 'w-full flex-1'} ${col} ${f.className ?? ''}`}
                        >
                          <Label
                            text={f.label}
                            isRequired={!!f.required}
                            className="text-base text-secondary-400 font-semibold leading-6 mb-xs block"
                          />
                          <SelectDropdown
                            label=""
                            placeholder="Select one"
                            options={opts}
                            value={(field.value as string) ?? ''}
                            onChange={(val) => {
                              field.onChange(val);
                              form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                            }}
                            onBlur={() => {
                              field.onBlur();
                              form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                            }}
                            className="w-full"
                            status={fieldState.error ? 'error' : 'default'}
                          />
                          {fieldState.error && (
                            <Typography variant="bodySm" className="mt-xs !text-xs !text-error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </div>
                      )}
                    />
                  );
                }
                case FORM_FIELD_KINDS.TEXTAREA: {
                  const maxLen = f.maxLength ?? maxLengthDefault;
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      render={({ field, fieldState }) => {
                        const value = (field.value as string) ?? '';
                        return (
                          <div className={`w-full space-y-xs ${col} ${f.className ?? ''}`}>
                            <Label
                              text={f.label}
                              isRequired={!!f.required}
                              className="text-base font-semibold leading-6 mb-xs block"
                            />
                            <TextArea
                              id={String(f.name)}
                              placeholder={
                                (f.label ? `Enter your ${f.label.toLowerCase()}` : '') ||
                                f.placeholder ||
                                placeholderFallbacks[f.name as string] ||
                                ''
                              }
                              value={value}
                              onChange={(newValue) => {
                                const safeValue = newValue.slice(0, maxLen);
                                field.onChange(safeValue);
                                onMessageChange(safeValue);
                              }}
                              onBlur={() => {
                                field.onBlur();
                                form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                              }}
                              status={fieldState.error ? 'error' : 'default'}
                              errorMessage={fieldState.error?.message}
                            />
                          </div>
                        );
                      }}
                    />
                  );
                }
                case FORM_FIELD_KINDS.CHECKBOX:
                default:
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      render={({ field, fieldState }) => (
                        <div className={`space-y-xs ${col} ${f.className ?? ''}`}>
                          <CheckBox
                            variant="default"
                            isChecked={!!field.value}
                            onChange={(checked) => {
                              field.onChange(checked);
                              form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                            }}
                            isRequired={!!f.required}
                            iconName="Tick"
                            boxClassName="w-5 h-5"
                            iconSize={16}
                          >
                            <span className="text-sm font-body text-foreground text-secondary-500">{f.label}</span>
                          </CheckBox>
                          {f.description && (
                            <Typography variant="bodyXs" className="text-secondary-500">
                              {f.description}
                            </Typography>
                          )}
                          {fieldState.error && (
                            <Typography variant="bodySm" className="mt-xs !text-xs !text-error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </div>
                      )}
                    />
                  );
              }
            })}
          </div>

          <div className="space-y-sm">
            {children}

            {/* Submit Button */}
            <Button
              type="submit"
              isDisabled={submitting}
              variant="primary"
              className="flex w-full md:w-60 md:min-w-5xl h-11 min-h-11 max-h-11 px-sm py-button items-center justify-center gap-xs rounded-sm"
            >
              {submitting ? (
                <>
                  <Spinner size="small" className="text-primary" />
                  <span className="text-white font-sora text-base font-semibold leading-5">Sending...</span>
                </>
              ) : (
                <span className="block w-full text-center text-white font-sora text-base font-semibold leading-5">
                  Submit
                </span>
              )}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
